import { useState } from 'react';
import { Card, Tabs, Space, Button, Tooltip, Empty, Input } from 'antd';
import {
  BoxPlotOutlined,
  BorderOutlined,
  CrownOutlined,
  ClusterOutlined,
  PlusOutlined,
  SearchOutlined,
  DotChartOutlined,
  TableOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import useSelectionStore from '../../stores/selectionStore';
import { generateId } from '@dtwin/shared';
import type { Model3D } from '@dtwin/shared';
import { createModel } from '../../api/models';
import { message } from 'antd';

const { Search } = Input;

// ============================================
// 预设模型定义
// ============================================

interface PresetModel {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: 'basic' | 'shape' | 'light';
  config: {
    type: string;
    args?: any[];
    color?: string;
  };
}

const PRESET_MODELS: PresetModel[] = [
  // 基础几何体
  {
    id: 'cube',
    name: '立方体',
    icon: <BorderOutlined />,
    category: 'basic',
    config: { type: 'boxGeometry', args: [1, 1, 1] },
  },
  {
    id: 'sphere',
    name: '球体',
    icon: <DotChartOutlined />,
    category: 'basic',
    config: { type: 'sphereGeometry', args: [0.5, 32, 32] },
  },
  {
    id: 'cylinder',
    name: '圆柱体',
    icon: <TableOutlined />,
    category: 'basic',
    config: { type: 'cylinderGeometry', args: [0.5, 0.5, 1, 32] },
  },
  {
    id: 'cone',
    name: '圆锥体',
    icon: <CrownOutlined />,
    category: 'basic',
    config: { type: 'coneGeometry', args: [0.5, 1, 32] },
  },
  {
    id: 'torus',
    name: '圆环',
    icon: <ClusterOutlined />,
    category: 'basic',
    config: { type: 'torusGeometry', args: [0.5, 0.2, 16, 100] },
  },
  {
    id: 'plane',
    name: '平面',
    icon: <BoxPlotOutlined />,
    category: 'basic',
    config: { type: 'planeGeometry', args: [1, 1] },
  },
  // 形状
  {
    id: 'torus-knot',
    name: '环面结',
    icon: <ClusterOutlined />,
    category: 'shape',
    config: { type: 'torusKnotGeometry', args: [0.5, 0.15, 100, 16] },
  },
  {
    id: 'dodecahedron',
    name: '十二面体',
    icon: <BoxPlotOutlined />,
    category: 'shape',
    config: { type: 'dodecahedronGeometry', args: [0.5] },
  },
  {
    id: 'icosahedron',
    name: '二十面体',
    icon: <BoxPlotOutlined />,
    category: 'shape',
    config: { type: 'icosahedronGeometry', args: [0.5] },
  },
  {
    id: 'octahedron',
    name: '八面体',
    icon: <BoxPlotOutlined />,
    category: 'shape',
    config: { type: 'octahedronGeometry', args: [0.5] },
  },
  {
    id: 'tetrahedron',
    name: '四面体',
    icon: <BoxPlotOutlined />,
    category: 'shape',
    config: { type: 'tetrahedronGeometry', args: [0.5] },
  },
];

// ============================================
// ModelLibrary 组件
// ============================================

interface ModelLibraryProps {
  onAddModel?: (model: PresetModel) => void;
}

export default function ModelLibrary({ onAddModel }: ModelLibraryProps) {
  const [searchValue, setSearchValue] = useState('');
  const [activeCategory, setActiveCategory] = useState('basic');
  const { addModel, currentScene } = useSceneStore();
  const { select } = useSelectionStore();
  
  // 过滤模型
  const filteredModels = PRESET_MODELS.filter(
    (model) =>
      model.category === activeCategory &&
      model.name.toLowerCase().includes(searchValue.toLowerCase())
  );
  
  // 添加预设模型到场景：构造合法 Model3D（format=preset + presetId），并落库
  const handleAddModel = async (preset: PresetModel) => {
    const nodeId = generateId();
    
    const modelData: Omit<Model3D, 'sceneId'> = {
      id: nodeId,
      name: preset.name,
      url: `preset://${preset.id}`,
      format: 'preset',
      presetId: preset.id,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visible: true,
      locked: false,
    };
    
    // 添加到场景
    addModel(modelData as any);
    
    // 落库（与复制模型一致，否则保存按钮的 PUT 会 404）
    if (currentScene) {
      createModel({
        sceneId: currentScene.id,
        name: preset.name,
        url: `preset://${preset.id}`,
        format: 'preset',
        presetId: preset.id,
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        visible: true,
        locked: false,
      } as any)
        .then(() => message.success(`已添加模型 ${preset.name}`))
        .catch(() => {});
    }
    
    // 选中新添加的模型
    select(nodeId);
    
    // 回调
    onAddModel?.(preset);
  };
  
  return (
    <div className="model-library" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 搜索框 */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #30475e' }}>
        <Search
          placeholder="搜索模型"
          size="small"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ background: '#1a1a2e', borderColor: '#30475e' }}
        />
      </div>
      
      {/* 分类标签 */}
      <Tabs
        activeKey={activeCategory}
        onChange={setActiveCategory}
        size="small"
        items={[
          {
            key: 'basic',
            label: '基础',
          },
          {
            key: 'shape',
            label: '形状',
          },
        ]}
        style={{ padding: '0 8px' }}
      />
      
      {/* 模型网格 */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '8px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
          alignContent: 'start',
        }}
      >
        {filteredModels.length === 0 ? (
          <Empty
            description="未找到模型"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ gridColumn: 'span 3' }}
          />
        ) : (
          filteredModels.map((model) => (
            <Tooltip key={model.id} title={model.name}>
              <Card
                hoverable
                size="small"
                onClick={() => handleAddModel(model)}
                style={{
                  background: '#1a1a2e',
                  borderColor: '#30475e',
                  cursor: 'pointer',
                }}
                styles={{ body: { padding: 8, textAlign: 'center' } }}
              >
                <div style={{ fontSize: 24, color: '#1890ff', marginBottom: 4 }}>
                  {model.icon}
                </div>
                <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {model.name}
                </div>
              </Card>
            </Tooltip>
          ))
        )}
      </div>
      
      {/* 添加按钮 */}
      <div style={{ padding: '8px 12px', borderTop: '1px solid #30475e' }}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          block
          size="small"
          style={{ borderColor: '#30475e', color: '#888' }}
          onClick={() => {
            // 添加默认立方体
            const defaultModel = PRESET_MODELS.find((m) => m.id === 'cube');
            if (defaultModel) {
              handleAddModel(defaultModel);
            }
          }}
        >
          添加默认模型
        </Button>
      </div>
    </div>
  );
}

// ============================================
// ModelListItem 组件
// ============================================

interface ModelListItemProps {
  model: {
    id: string;
    name: string;
    format?: string;
    visible?: boolean;
    locked?: boolean;
  };
  isSelected: boolean;
  onSelect: (id: string, multi?: boolean) => void;
  onToggleVisibility: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
}

export function ModelListItem({
  model,
  isSelected,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onDelete,
}: ModelListItemProps) {
  const getModelIcon = (format?: string) => {
    switch (format?.toLowerCase()) {
      case 'glb':
      case 'gltf':
        return <BoxPlotOutlined style={{ color: '#1890ff' }} />;
      case 'fbx':
        return <BoxPlotOutlined style={{ color: '#52c41a' }} />;
      case 'obj':
        return <BoxPlotOutlined style={{ color: '#faad14' }} />;
      case 'stl':
        return <BoxPlotOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <BoxPlotOutlined style={{ color: '#888' }} />;
    }
  };
  
  return (
    <div
      className={`model-list-item ${isSelected ? 'selected' : ''}`}
      onClick={(e) => onSelect(model.id, e.ctrlKey || e.metaKey)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        cursor: 'pointer',
        background: isSelected ? '#1890ff22' : 'transparent',
        borderRadius: 4,
        marginBottom: 2,
      }}
    >
      <span style={{ marginRight: 8, fontSize: 16 }}>
        {getModelIcon(model.format)}
      </span>
      <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {model.name}
      </span>
      <Space size={2}>
        <Tooltip title={model.visible !== false ? '隐藏' : '显示'}>
          <Button
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(model.id);
            }}
            style={{ color: model.visible !== false ? '#1890ff' : '#666', width: 20, height: 20 }}
          >
            {model.visible !== false ? '👁' : '👁‍🗨'}
          </Button>
        </Tooltip>
        <Tooltip title={model.locked ? '解锁' : '锁定'}>
          <Button
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleLock(model.id);
            }}
            style={{ color: model.locked ? '#ff4d4f' : '#666', width: 20, height: 20 }}
          >
            {model.locked ? '🔒' : '🔓'}
          </Button>
        </Tooltip>
        <Tooltip title="删除">
          <Button
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(model.id);
            }}
            style={{ color: '#ff4d4f', width: 20, height: 20 }}
          >
            🗑
          </Button>
        </Tooltip>
      </Space>
    </div>
  );
}
